import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { images, trustpilot } from "../data/content";

const headlineLines = ["Supporting you", "throughout your IVA"];

const LineReveal = ({ text, delay, reduce }) => (
  <span className="hero-line-mask">
    <motion.span
      className="block"
      initial={reduce ? false : { y: "110%" }}
      animate={reduce ? {} : { y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export const HeroSection = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative overflow-hidden bg-brand-bg"
      aria-labelledby="hero-heading"
    >
      <div className="mx-auto grid max-w-content items-center gap-10 px-4 py-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 lg:py-12 lg:px-8">
        <div className="max-w-xl">
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={reduce ? {} : { opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-border bg-white px-4 py-1.5 text-sm font-semibold text-brand-teal"
          >
            <span className="h-2 w-2 rounded-full bg-brand-teal" aria-hidden="true" />
            Helping people manage their debt for over 25 years
          </motion.p>

          <h1
            id="hero-heading"
            className="font-heading text-4xl font-bold leading-[1.05] tracking-tight text-brand-navy sm:text-5xl lg:text-6xl"
          >
            {headlineLines.map((line, i) => (
              <LineReveal key={line} text={line} delay={0.15 + i * 0.14} reduce={reduce} />
            ))}
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={reduce ? {} : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 text-lg leading-relaxed text-brand-body"
          >
            Our experienced team is here to help you understand and manage your Individual
            Voluntary Arrangement — with clear guidance at every stage.
          </motion.p>

          <motion.a
            href={trustpilot.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={reduce ? {} : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-6 inline-flex items-center gap-4 rounded-2xl border border-brand-border bg-white px-5 py-3 shadow-sm transition-shadow hover:shadow-md"
            data-testid="hero-trustpilot"
            aria-label={`Rated ${trustpilot.score} out of 5, ${trustpilot.label}, on Trustpilot from ${trustpilot.total} reviews — opens in a new tab`}
          >
            <span className="flex flex-col">
              <span className="font-heading text-2xl font-bold leading-none text-brand-navy">
                {trustpilot.score}
                <span className="text-base font-semibold text-brand-body">/5.0</span>
              </span>
              <span className="mt-1 text-xs font-semibold uppercase tracking-wide text-[#00b67a]">
                {trustpilot.label}
              </span>
            </span>
            <span className="h-9 w-px bg-brand-border" aria-hidden="true" />
            <span className="flex flex-col">
              <span className="flex gap-0.5" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className="grid place-items-center rounded-[3px] bg-[#00b67a]">
                    <Star className="h-4 w-4 fill-white p-0.5 text-white" />
                  </span>
                ))}
              </span>
              <span className="mt-1 text-xs font-semibold text-brand-body">
                Rated on Trustpilot · {trustpilot.total} reviews
              </span>
            </span>
          </motion.a>
        </div>

        <div className="relative">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={reduce ? {} : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[28px] border border-brand-border bg-white shadow-lg"
          >
            <motion.img
              style={reduce ? {} : { y: imgY, scale: 1.12 }}
              src={images.hero}
              alt="A relaxed couple reviewing household paperwork together at home."
              width="1264"
              height="848"
              className="h-[300px] w-full object-cover sm:h-[340px] lg:h-[380px]"
              loading="eager"
              fetchPriority="high"
            />
          </motion.div>

          <div className="absolute -bottom-5 left-5 hidden rounded-2xl border border-brand-border bg-white px-5 py-4 shadow-md sm:block">
            <p className="font-heading text-2xl font-bold text-brand-navy">Here for you</p>
            <p className="text-sm text-brand-body">at every stage of your IVA</p>
          </div>
        </div>
      </div>
    </section>
  );
};
